import { pluralReceipts } from "@/utils/format";
import type { ReadinessCounts } from "@/utils/receiptReadiness";
import "./incomplete-receipts-notice.css";

export interface IncompleteReceiptsNoticeProps {
  counts: ReadinessCounts;
  /** True when every needs-attention receipt is still processing (no link). */
  transientOnly: boolean;
  /** Navigate to the receipts tab filtered to "потребують уваги". */
  onResolve: () => void;
  /** Closed trip: link copy switches to the reopen path (AC-16.11). */
  tripClosed?: boolean;
}

/**
 * Calm, blue `info` inline notice (component §4.2a) warning that the
 * «Розрахунок» plan is built on an incomplete receipt set (ADR-0029). It is
 * NOT a banner and is visually distinct from the yellow <ProvisionalBanner> —
 * the FX-provisional axis (rates not locked) and the data-incomplete axis
 * (some costs not counted) must read as two separate statements (AC-16.10).
 *
 * Renders only when counts.total > 0 — the caller guards this, but we also
 * return null defensively so callers can render unconditionally.
 */
export function IncompleteReceiptsNotice({
  counts,
  transientOnly,
  onResolve,
  tripClosed = false,
}: IncompleteReceiptsNoticeProps) {
  if (counts.total <= 0) return null;

  // Transient-only: nothing to fix yet, so no link — just a calm "ще
  // обробляється" line. Plural on the "чек(и)" noun via pluralReceipts.
  if (transientOnly) {
    return (
      <aside
        className="wp-incomplete"
        role="status"
        aria-live="polite"
        data-testid="incomplete-receipts-notice"
      >
        <span aria-hidden="true" className="wp-incomplete__icon">
          ⓘ
        </span>
        <div className="wp-incomplete__body">
          <p className="wp-incomplete__text">
            {pluralReceipts(counts.total)} ще обробляється — щойно буде готовий,
            додам його до плану.
          </p>
        </div>
      </aside>
    );
  }

  const title = `${pluralReceipts(counts.total)} ще не враховані`;
  const body = buildBody(counts);
  const linkLabel = tripClosed
    ? "Відкрити поїздку, щоб додати →"
    : "Завершити чеки →";

  return (
    <aside
      className="wp-incomplete"
      role="status"
      aria-live="polite"
      data-testid="incomplete-receipts-notice"
    >
      <span aria-hidden="true" className="wp-incomplete__icon">
        ⓘ
      </span>
      <div className="wp-incomplete__body">
        <p className="wp-incomplete__title">{title}</p>
        <p className="wp-incomplete__text">{body}</p>
        <button
          type="button"
          className="wp-incomplete__link"
          onClick={onResolve}
          data-testid="incomplete-receipts-resolve"
        >
          {linkLabel}
        </button>
      </div>
    </aside>
  );
}

/**
 * Reason copy (wireframe settle-up.md §ACCEPTED):
 * - When the only reasons are the user-fixable missing-field ones (no-payer /
 *   no-currency), use the warmer combined sentence «Не вказано, хто платив,
 *   або не вибрано валюту — ці суми не входять у план.»
 * - When a processing/failed/unconfirmed receipt is mixed in, itemise each
 *   non-zero bucket («1 ще обробляється, 1 не розпізнався, 1 не підтверджено.»)
 *   so the visitor knows the shape of the work.
 */
function buildBody(counts: ReadinessCounts): string {
  const hasOther =
    counts.processing > 0 || counts.failed > 0 || counts.unconfirmed > 0;

  if (!hasOther) {
    // Only missing-field reasons present (no-payer and/or no-currency).
    return "Не вказано, хто платив, або не вибрано валюту — ці суми не входять у план.";
  }

  const fragments: string[] = [];
  if (counts.processing > 0) {
    fragments.push(`${counts.processing} ще обробляється`);
  }
  if (counts.failed > 0) {
    fragments.push(`${counts.failed} не розпізнався`);
  }
  if (counts.noPayer > 0) {
    fragments.push(`${counts.noPayer} без платника`);
  }
  if (counts.noCcy > 0) {
    fragments.push(`${counts.noCcy} без валюти`);
  }
  if (counts.unconfirmed > 0) {
    fragments.push(`${counts.unconfirmed} не підтверджено`);
  }
  return `${fragments.join(", ")}.`;
}
